import React from 'react'
import { LegalHeader } from './LegalHeader'

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-lynx text-dusk">
      <LegalHeader />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <p className="mb-4">
          This Privacy Policy describes how nura collects, uses, and shares information about you when you
          use our meal planning Service. By using the Service, you consent to this Policy.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">1. Information We Collect</h2>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            Account Information: email address and authentication data required to create and manage your
            account.
          </li>
          <li>
            Profile & Preferences: household size, dietary preferences, cooking skill/time, and similar
            settings to personalize plans.
          </li>
          <li>
            Usage & Analytics: events related to navigation, plan generation, and recipe swipes. We store
            lightweight event data (e.g., timestamps, page domains, non‑PII context) to improve the Service.
          </li>
          <li>
            Payment Data: handled by trusted third-party payment processors. We receive limited
            metadata (e.g., subscription status, plan tier) but do not store full card details.
          </li>
          <li>
            Communications: your email interactions (e.g., verification, receipts, weekly plan emails) via
            reliable email service providers.
          </li>
          <li>
            AI Inputs: prompts and context you provide for AI‑assisted features may be sent to 
            industry-leading AI service providers for processing.
          </li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">2. How We Use Information</h2>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>Provide and personalize meal plans, recommendations, and related features.</li>
          <li>Authenticate users, secure the Service, and prevent misuse.</li>
          <li>Process subscriptions, trials, and billing through third‑party processors.</li>
          <li>Send transactional emails (verification, receipts, plan emails) and optional product updates.</li>
          <li>Analyze usage to improve reliability, UX, and performance.</li>
          <li>Comply with legal obligations and enforce our Terms.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">3. Legal Bases</h2>
        <p className="mb-4">
          Where required (e.g., in the EEA/UK), we rely on contract performance, legitimate interests
          (security, analytics, product improvement), consent (where applicable), and legal obligations.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">4. Sharing of Information</h2>
        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>Trusted payment processors for subscription management and billing.</li>
          <li>Professional email service providers for transactional communications.</li>
          <li>Industry-leading AI technology partners to generate recommendations and personalized features.</li>
          <li>Analytics tools used in a privacy‑respecting manner; we avoid sensitive data collection.</li>
          <li>Essential service providers (infrastructure, databases) under appropriate security and confidentiality terms.</li>
          <li>Legal or safety reasons if required by law or to protect rights and safety.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">5. Cookies & Similar Technologies</h2>
        <p className="mb-4">
          We use cookies/local storage for session management and to store limited analytics context (e.g.,
          first referrer). You can adjust your browser settings to limit cookies.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">6. Data Retention</h2>
        <p className="mb-4">
          We retain your account data while you maintain an account. We retain lightweight analytics events
          for limited periods (e.g., 60 days) for product improvement and reliability.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">7. Security</h2>
        <p className="mb-4">
          We employ administrative, technical, and physical safeguards appropriate for the data we handle.
          No method of transmission or storage is completely secure.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">8. International Transfers</h2>
        <p className="mb-4">
          Your data may be processed in any country where we or our providers operate. We use appropriate
          safeguards for cross‑border transfers where required.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">9. Your Rights</h2>
        <p className="mb-2">
          Depending on your location, you may have rights to access, correct, delete, or port your personal
          data, object to or restrict certain processing, or withdraw consent where processing is based on
          consent.
        </p>
        <p className="mb-4">
          To exercise rights, contact us at <a className="text-warning-700 underline" href="mailto:privacy@nura.app">privacy@nura.app</a>.
          We may need to verify your identity before responding.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">10. Children’s Privacy</h2>
        <p className="mb-4">The Service is not directed to children under 13, and we do not knowingly collect their data.</p>

        <h2 className="text-xl font-semibold mt-8 mb-2">11. Changes to this Policy</h2>
        <p className="mb-4">
          We may update this Policy from time to time. If we make material changes, we will notify you (e.g.,
          in‑app or by email). Your continued use constitutes acceptance of the updated Policy.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">12. Contact</h2>
        <p className="mb-8">
          Questions about privacy? Contact <a className="text-warning-700 underline" href="mailto:privacy@nura.app">privacy@nura.app</a>.
        </p>

        <p className="text-xs text-muted-foreground">
          This template is provided for convenience only and does not constitute legal advice. Consult your
          legal counsel to tailor this Policy for your jurisdiction and business.
        </p>
      </div>
    </div>
  )
}
